import { LabelSize } from 'label/utils/types';
import { HubState } from 'root/apps/pages/store/reducers/hub';

export enum HUB_MESSAGES {
  GET_CONFIG = 'GET_CONFIG',
  GET_CHANNEL_NAME = 'GET_CHANNEL_NAME',
  UPDATE_CHANNEL_NAME = 'UPDATE_CHANNEL_NAME',
  UPDATE_CONFIG = 'CONFIG_UPDATED',
  DISCONNECT = 'DISCONNECT',
  DEVICES = 'DEVICES',
}

export const isHubMessage = (str: string): str is HUB_MESSAGES =>
  Object.values(HUB_MESSAGES).includes(str as HUB_MESSAGES);

export enum CFD_MESSAGES {
  UPDATE_SALE = 'SALE_UPDATED',
  COMPLETE_SALE = 'SALE_COMPLETED',
  HIDE_SALE = 'HIDE_SALE',
  SHOW_SALE = 'SHOW_SALE',
  CLOSE = 'CLOSE',
  TIP_FLOW_START = 'TIP_FLOW_START',
  TIP_CONFIRMED = 'TIP_CONFIRMED',
  CUSTOMER_START = 'CUSTOMER_START',
  CUSTOMER_SAVE = 'CUSTOMER_SAVE',
  CUSTOMER_CANCEL = 'CUSTOMER_CANCEL',
}

export const isCfdMessage = (str: string): str is CFD_MESSAGES =>
  Object.values(CFD_MESSAGES).includes(str as CFD_MESSAGES);

export enum PRINTER_MESSAGES {
  PRINT = 'PRINT',
  PRINT_COMPLETED = 'PRINT_COMPLETED',
  PRINT_ERROR = 'PRINT_ERROR',
  OPEN_CASH_DRAWER = 'OPEN_CASH_DRAWER',
}

export const isPrinterMessage = (str: string): str is PRINTER_MESSAGES =>
  Object.values(PRINTER_MESSAGES).includes(str as PRINTER_MESSAGES);

export type ConnectionStatus = 'Connected' | 'Disconnected' | 'Error';
export type AblyStatus = ConnectionStatus | 'Ready';

const createIsStatus = (
  isStatus: ConnectionStatus | AblyStatus
): ((status: ConnectionStatus | AblyStatus) => boolean) => {
  return (status) => isStatus === status;
};

export const isStatusConnected = createIsStatus('Connected');
export const isStatusDisconnected = createIsStatus('Disconnected');
export const isStatusError = createIsStatus('Error');
export const isStatusReady = createIsStatus('Ready');

export enum PRINTER_KINDS {
  RECEIPT = 'RECEIPT',
  LABEL = 'LABEL',
}

export enum PRINTER_FORMATS {
  URL = 'URL',
  BUFFER = 'BUFFER',
  HTML = 'HTML',
  ZPL = 'ZPL',
}

export enum PRINTER_STATUSES {
  READY = 'READY',
  COVER_OPEN = 'COVER_OPEN',
  LOW_PAPER = 'LOW_PAPER',
  NO_PAPER = 'NO_PAPER',
  PAPER_JAM = 'PAPER_JAM',
  ERROR = 'ERROR',
  DISCONNECTED = 'DISCONNECTED',
  NOT_AVAILABLE = 'NOT_AVAILABLE',
  PAUSED = 'PAUSED',
  OVERHEATED = 'OVERHEATED',
  NOT_RESPONDING = 'NOT_RESPONDING',
}

export enum MEMBER_TYPE {
  RETAIL = 'RETAIL',
  CFD = 'CFD',
  HUB = 'HUB',
}

export interface PrintData {
  data: string;
  kind: PRINTER_KINDS;
  executionTimes: Record<string, number>;
  format: PRINTER_FORMATS;
  openCashDrawer?: boolean;
  size?: LabelSize;
  copies?: number;
  analyticsData?: Record<string, any>;
}

interface CFDDevices {
  defaultPrinters: undefined;
  selectedDisplay: HubState['selectedDisplay'];
  version?: string;
}

interface HubDevices {
  defaultPrinters: HubState['defaultPrinters'];
  selectedDisplay: HubState['selectedDisplay'];
  version?: string;
}

type SendReturn = Promise<boolean | void>;

export interface AbstractSocket {
  connect: () => void;
  disconnect: () => void;
  send(event: PRINTER_MESSAGES.PRINT, data: PrintData): SendReturn;
  send(event: PRINTER_MESSAGES.OPEN_CASH_DRAWER): SendReturn;
  send(event: CFD_MESSAGES.HIDE_SALE): SendReturn;
  send(event: CFD_MESSAGES.SHOW_SALE): SendReturn;
  send(event: CFD_MESSAGES.CLOSE): SendReturn;
  send(event: CFD_MESSAGES.UPDATE_SALE, data: Record<string, unknown>): SendReturn;
  send(event: CFD_MESSAGES.COMPLETE_SALE, data?: Record<string, unknown>): SendReturn;
  send(event: CFD_MESSAGES.TIP_FLOW_START, data: { sale: any; tip: any }): SendReturn;
  send(event: CFD_MESSAGES.CUSTOMER_START): SendReturn;
  send(event: HUB_MESSAGES.DISCONNECT): SendReturn;
  send(event: HUB_MESSAGES.DEVICES, data: Record<any, any>): SendReturn;
  send(event: HUB_MESSAGES.UPDATE_CHANNEL_NAME, data: string): SendReturn;
  send(event: HUB_MESSAGES.UPDATE_CONFIG, data: { config: Record<any, any> }): SendReturn;
}
/**
 * Message types sent from retail to the hub
 */
export type RetailMessages =
  | PRINTER_MESSAGES.PRINT
  | PRINTER_MESSAGES.OPEN_CASH_DRAWER
  | CFD_MESSAGES.HIDE_SALE
  | CFD_MESSAGES.SHOW_SALE
  | CFD_MESSAGES.CLOSE
  | CFD_MESSAGES.UPDATE_SALE
  | CFD_MESSAGES.COMPLETE_SALE
  | CFD_MESSAGES.TIP_FLOW_START
  | CFD_MESSAGES.CUSTOMER_START
  | HUB_MESSAGES.DISCONNECT
  | HUB_MESSAGES.DEVICES
  | HUB_MESSAGES.UPDATE_CHANNEL_NAME
  | HUB_MESSAGES.UPDATE_CONFIG;

export type Devices = CFDDevices | HubDevices;

export type Transport = 'Ably' | 'LocalServer' | null;
